import React, { useState, useMemo, useEffect } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import axios from "axios";
import Swal from "sweetalert2";
import { baseurl, imgurl } from "../baseurl";
import { FaFilter, FaBoxOpen, FaCheckCircle, FaTimesCircle, FaStore, FaTag, FaCalendarAlt } from "react-icons/fa";

function ManageSku() {
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    vendor: "All",
    category: "All",
    status: "All",
    priceMin: "",
    priceMax: "",
    dateFrom: null,
    dateTo: null,
  });
  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${baseurl}getAllProductsForAdmin`);
      if (res.data && res.data.products) {
        setProducts(res.data.products);
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Load Failed", text: err.message });
    } finally {
      setLoading(false);
    }
  };
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const toggleSkuAssured = async (productUuid, currentValue) => {
    const newValue = currentValue === "Yes" ? "No" : "Yes";
    const confirm = await Swal.fire({
      title: `Set SKU Assured to ${newValue}?`,
      text: "This badge signifies verified quality and faster shipping to customers.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Update Badge",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.put(`${baseurl}updateSkuAssured/${productUuid}`, { sku_assured: newValue });
      if (res.data.success) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: `Quality status: ${newValue}`,
          showConfirmButton: false,
          timer: 1500,
        });
        setProducts((prev) => prev.map((p) => p.uuid === productUuid ? { ...p, sku_assured: newValue } : p));
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: "Update failed" });
    }
  };

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesSearch = p.product_name.toLowerCase().includes(filters.search.toLowerCase()) || p.sku.toLowerCase().includes(filters.search.toLowerCase());
      const matchesVendor = filters.vendor === "All" || p.vendor_name === filters.vendor;
      const matchesCategory = filters.category === "All" || p.category_name === filters.category;
      const matchesStatus = filters.status === "All" || p.status === filters.status.toLowerCase();
      const matchesPrice = (filters.priceMin === "" || Number(p.price) >= Number(filters.priceMin)) && (filters.priceMax === "" || Number(p.price) <= Number(filters.priceMax));
      const matchesDate = (!filters.dateFrom || new Date(p.created_at) >= new Date(filters.dateFrom)) && (!filters.dateTo || new Date(p.created_at) <= new Date(filters.dateTo));
      return matchesSearch && matchesVendor && matchesCategory && matchesStatus && matchesPrice && matchesDate;
    });
  }, [products, filters]);

  const vendors = ["All", ...new Set(products.map((p) => p.vendor_name))];
  const categories = ["All", ...new Set(products.map((p) => p.category_name))];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        <div className="spinner-grow text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light">
        <div className="main-content p-3 p-lg-4">
          
          {/* Header & Meta Summary */}
          <div className="row mb-4 align-items-center">
            <div className="col-md-8">
              <h4 className="fw-bold mb-1">Stock Keeping Units (SKU)</h4>
              <p className="text-muted small mb-0">Monitor inventory health, manage quality assurance badges, and track merchant stock across all categories.</p>
            </div>
            <div className="col-md-4 text-md-end mt-3 mt-md-0">
              <button className="btn btn-primary btn-sm shadow-sm px-3" onClick={() => setShowFilter(true)}>
                <FaFilter className="me-2" size={12} /> Advanced Search
              </button>
            </div>
          </div>

          {/* KPI Mini Cards */}
          <div className="row g-3 mb-4 text-center">
            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm p-2 rounded-3">
                <small className="text-muted d-block extra-small fw-bold">TOTAL SKUS</small>
                <span className="fw-bold">{products.length}</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm p-2 rounded-3">
                <small className="text-muted d-block extra-small fw-bold">QUALITY ASSURED</small>
                <span className="fw-bold text-success">{products.filter(p => p.sku_assured === 'Yes').length}</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm p-2 rounded-3">
                <small className="text-muted d-block extra-small fw-bold">ACTIVE ITEMS</small>
                <span className="fw-bold text-primary">{products.filter(p => p.status === 'active').length}</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card border-0 shadow-sm p-2 rounded-3">
                <small className="text-muted d-block extra-small fw-bold">MATCHED RESULTS</small>
                <span className="fw-bold text-indigo">{filteredProducts.length}</span>
              </div>
            </div>
          </div>

          {/* Main Inventory Table */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table align-middle table-hover mb-0 sku-table">
                <thead className="bg-white border-bottom">
                  <tr>
                    <th className="ps-4">Product Info</th>
                    <th>Identifier</th>
                    <th>Merchant & Category</th>
                    <th>Pricing</th>
                    <th>Assurance</th>
                    <th>Lifecycle</th>
                    <th className="text-end pe-4">Quality Control</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p) => (
                    <tr key={p.uuid}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          <img src={`${imgurl}${p.featured_image}`} alt="" className="rounded-3 me-3" style={{ width: "42px", height: "42px", objectFit: "cover" }} />
                          <div>
                            <div className="fw-bold text-dark small text-truncate" style={{ maxWidth: "180px" }}>{p.product_name}</div>
                            <div className="extra-small text-muted text-uppercase fw-bold">ID: {p.id}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                         <span className="badge bg-light text-dark border font-monospace px-2 py-1">{p.sku}</span>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                            <span className="small text-dark"><FaStore className="me-1 text-muted" size={10} /> {p.vendor_name}</span>
                            <span className="extra-small text-muted text-uppercase fw-bold"><FaTag className="me-1" size={10} /> {p.category_name}</span>
                        </div>
                      </td>
                      <td>
                         <div className="fw-bold text-dark">₹{Number(p.price).toLocaleString()}</div>
                      </td>
                      <td>
                        <span className={`status-pill ${p.sku_assured === "Yes" ? "verified" : "unverified"}`}>
                            {p.sku_assured === "Yes" ? "Assured Quality" : "Standard"}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-soft ${p.status === "active" ? "active" : "inactive"}`}>{p.status}</span>
                        <div className="extra-small text-muted mt-1"><FaCalendarAlt size={9} className="me-1"/>{new Date(p.created_at).toLocaleDateString("en-GB")}</div>
                      </td>
                      <td className="text-end pe-4">
                        <button 
                          className={`btn btn-sm fw-bold px-3 border-0 ${p.sku_assured === "Yes" ? "btn-soft-warning" : "btn-soft-success"}`}
                          onClick={() => toggleSkuAssured(p.uuid, p.sku_assured)}
                        >
                          {p.sku_assured === "Yes" ? "Revoke Assured" : "Grant Assured"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredProducts.length === 0 && (
                <div className="text-center py-5">
                    <p className="text-muted">No products matched your filters.</p>
                </div>
            )}
          </div>

          {/* Filter Panel (Slide-out) */}
          <div className={`filter-panel-overlay ${showFilter ? "visible" : ""}`} onClick={() => setShowFilter(false)}></div>
          <div className={`filter-panel-content ${showFilter ? "open" : ""}`}>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
              <h5 className="fw-bold mb-0">Search Parameters</h5>
              <button className="btn-close" onClick={() => setShowFilter(false)}></button>
            </div>
            
            <div className="mb-3">
              <label className="extra-small fw-bold text-uppercase text-muted">Keywords</label>
              <input type="text" className="form-control form-control-sm bg-light border-0" name="search" placeholder="Name or SKU..." value={filters.search} onChange={handleFilterChange} />
            </div>

            <div className="row g-2 mb-3">
              <div className="col-6">
                <label className="extra-small fw-bold text-uppercase text-muted">Merchant</label>
                <select className="form-select form-select-sm bg-light border-0" name="vendor" value={filters.vendor} onChange={handleFilterChange}>
                  {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="col-6">
                <label className="extra-small fw-bold text-uppercase text-muted">Category</label>
                <select className="form-select form-select-sm bg-light border-0" name="category" value={filters.category} onChange={handleFilterChange}>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="extra-small fw-bold text-uppercase text-muted">Availability Status</label>
              <div className="d-flex gap-2">
                 {['All', 'Active', 'Inactive'].map(s => (
                     <button key={s} className={`btn btn-sm flex-fill border ${filters.status === s ? 'btn-primary' : 'bg-white'}`} onClick={() => setFilters({...filters, status: s})}>{s}</button>
                 ))}
              </div>
            </div>

            <button className="btn btn-primary w-100 fw-bold" onClick={() => setShowFilter(false)}>Apply Configuration</button>
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 10px; }
        .sku-table thead th { font-size: 11px; text-transform: uppercase; color: #8898aa; letter-spacing: 0.5px; padding: 15px 10px; }
        .sku-table tbody td { padding: 10px; border-bottom: 1px solid #f6f9fc; font-size: 13px; }
        
        .status-pill { font-size: 10px; font-weight: 800; text-transform: uppercase; display: block; }
        .status-pill.verified { color: #10b981; }
        .status-pill.unverified { color: #94a3b8; }

        .badge-soft { padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; text-transform: uppercase; }
        .badge-soft.active { background: #dcfce7; color: #15803d; }
        .badge-soft.inactive { background: #fee2e2; color: #b91c1c; }

        .btn-soft-success { background: #f0fdf4; color: #16a34a; font-size: 11px; }
        .btn-soft-warning { background: #fff7ed; color: #ea580c; font-size: 11px; }
        .btn-soft-success:hover { background: #16a34a; color: #fff; }
        .btn-soft-warning:hover { background: #ea580c; color: #fff; }

        .filter-panel-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.3); z-index: 1050; opacity: 0; visibility: hidden; transition: 0.3s; }
        .filter-panel-overlay.visible { opacity: 1; visibility: visible; }
        .filter-panel-content { position: fixed; top: 0; right: -350px; width: 320px; height: 100%; background: white; z-index: 1051; padding: 25px; transition: 0.4s cubic-bezier(0.16, 1, 0.3, 1); box-shadow: -10px 0 30px rgba(0,0,0,0.1); }
        .filter-panel-content.open { right: 0; }
        
        .rounded-4 { border-radius: 0.75rem !important; }
        .text-indigo { color: #6366f1; }
      `}</style>
    </>
  );
}

export default ManageSku;